import "./eq-module.css";

import { Knob } from "./knob.ts";

// EqModule — 3-band parametric EQ (low shelf · parametric mid · high
// shelf). Five small knobs grouped into bands by hairline dividers, so
// "low / mid / high" reads at a glance even at compact size.
//
// Engine param paths (and units):
//   fx.eq.low_shelf_gain_db   — −18 .. +18 dB
//   fx.eq.mid_freq_hz         — 100 .. 8000 Hz, log
//   fx.eq.mid_gain_db         — −18 .. +18 dB
//   fx.eq.mid_q               — 0.2 .. 8
//   fx.eq.high_shelf_gain_db  — −18 .. +18 dB

export interface EqState {
  lowGainDb: number;
  midFreqHz: number;
  midGainDb: number;
  midQ: number;
  highGainDb: number;
}

export interface EqModuleOptions {
  container: HTMLElement;
  setParam: (path: string, value: number) => void;
  /** Partial initial state. Missing fields fall back to engine defaults. */
  initial?: Partial<EqState>;
}

export class EqModule {
  private root: HTMLDivElement;
  private lowKnob!: Knob;
  private midFreqKnob!: Knob;
  private midGainKnob!: Knob;
  private midQKnob!: Knob;
  private highKnob!: Knob;

  constructor(opts: EqModuleOptions) {
    const init = opts.initial ?? {};
    const lowGain = init.lowGainDb ?? 0;
    const midFreq = init.midFreqHz ?? 1000;
    const midGain = init.midGainDb ?? 0;
    const midQ = init.midQ ?? 0.707;
    const highGain = init.highGainDb ?? 0;

    this.root = document.createElement("div");
    this.root.className = "eq-module";

    const header = document.createElement("div");
    header.className = "eq-module-header fx-module-header";
    const title = document.createElement("span");
    title.className = "eq-module-title fx-module-title";
    title.textContent = "eq";
    header.append(title);
    this.root.append(header);

    const knobs = document.createElement("div");
    knobs.className = "eq-module-knobs";
    this.root.append(knobs);

    // Low shelf.
    const lowHost = document.createElement("div");
    knobs.append(lowHost);
    this.lowKnob = new Knob({
      container: lowHost,
      size: 40,
      min: -18,
      max: 18,
      default: 0,
      value: lowGain,
      label: "low",
      format: formatDb,
      onChange: (v) => opts.setParam("fx.eq.low_shelf_gain_db", v),
    });

    // Mid band — freq, gain, Q wrapped in their own group so the
    // hairline dividers read as "this is the parametric mid band".
    const midHost = document.createElement("div");
    midHost.className = "eq-module-mid";
    knobs.append(midHost);
    this.midFreqKnob = new Knob({
      container: midHost,
      size: 40,
      min: 100,
      max: 8000,
      default: 1000,
      value: midFreq,
      taper: "log",
      label: "mid hz",
      format: (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${v.toFixed(0)}`),
      onChange: (v) => opts.setParam("fx.eq.mid_freq_hz", v),
    });
    this.midGainKnob = new Knob({
      container: midHost,
      size: 40,
      min: -18,
      max: 18,
      default: 0,
      value: midGain,
      label: "mid db",
      format: formatDb,
      onChange: (v) => opts.setParam("fx.eq.mid_gain_db", v),
    });
    this.midQKnob = new Knob({
      container: midHost,
      size: 40,
      min: 0.2,
      max: 8,
      default: 0.707,
      value: midQ,
      taper: "log",
      label: "mid q",
      format: (v) => `q ${v.toFixed(1)}`,
      onChange: (v) => opts.setParam("fx.eq.mid_q", v),
    });

    // High shelf.
    const highHost = document.createElement("div");
    knobs.append(highHost);
    this.highKnob = new Knob({
      container: highHost,
      size: 40,
      min: -18,
      max: 18,
      default: 0,
      value: highGain,
      label: "high",
      format: formatDb,
      onChange: (v) => opts.setParam("fx.eq.high_shelf_gain_db", v),
    });

    opts.container.append(this.root);
  }

  getState(): EqState {
    return {
      lowGainDb: this.lowKnob.current,
      midFreqHz: this.midFreqKnob.current,
      midGainDb: this.midGainKnob.current,
      midQ: this.midQKnob.current,
      highGainDb: this.highKnob.current,
    };
  }

  setState(s: EqState): void {
    this.lowKnob.setValue(s.lowGainDb);
    this.midFreqKnob.setValue(s.midFreqHz);
    this.midGainKnob.setValue(s.midGainDb);
    this.midQKnob.setValue(s.midQ);
    this.highKnob.setValue(s.highGainDb);
  }

  destroy(): void {
    this.root.remove();
  }
}

function formatDb(v: number): string {
  if (Math.abs(v) < 0.05) return "0 db";
  return `${v >= 0 ? "+" : ""}${v.toFixed(1)} db`;
}
